import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Link } from "react-router";
import { Plus, Trash2, AlertCircle, MoreHorizontal } from "lucide-react";
import { useAdminAuth } from "@/lib/hooks/useAdminAuth";
import {
  listAppointmentServices,
  deleteAppointmentService,
  listStoreAppointments,
  cancelAppointment,
  markAppointmentNoShow,
  completeAppointment,
  imposeStoreAppointmentBan,
} from "@/lib/api/admin";
import type { AppointmentService, Appointment } from "@/lib/api/types";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Spinner } from "@/components/Spinner";
import { StatusBadge } from "@/components/StatusBadge";
import { Badge } from "@/components/ui/badge";
import { DataTable, type Column } from "@/components/DataTable";
import { Pagination } from "@/components/Pagination";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { formatDateTime } from "@/lib/utils/date";
import { formatCents, formatPoints } from "@/lib/utils/number";
import { PAGE_SIZE } from "@/lib/constants";
import { AppointmentServiceFormDialog } from "../dialogs/AppointmentServiceFormDialog";
import { StoreAppointmentPolicySection } from "../StoreAppointmentPolicySection";

type AppointmentsTabProps = {
  storeId: string;
  storeCategory: string;
  priceLimitBypass: boolean;
};

// ─── Main AppointmentsTab ─────────────────────────────────────────────────────

export function AppointmentsTab({
  storeId,
  storeCategory,
  priceLimitBypass,
}: AppointmentsTabProps) {
  return (
    <Tabs defaultValue="services">
      <TabsList>
        <TabsTrigger value="services">Services</TabsTrigger>
        <TabsTrigger value="bookings">Bookings</TabsTrigger>
        <TabsTrigger value="policy">Cancellation Policy</TabsTrigger>
      </TabsList>

      <TabsContent value="services" className="mt-4">
        <AppointmentServicesSection
          storeId={storeId}
          storeCategory={storeCategory}
          priceLimitBypass={priceLimitBypass}
        />
      </TabsContent>

      <TabsContent value="bookings" className="mt-4">
        <AppointmentBookingsSection storeId={storeId} />
      </TabsContent>

      <TabsContent value="policy" className="mt-4">
        <StoreAppointmentPolicySection storeId={storeId} />
      </TabsContent>
    </Tabs>
  );
}

// ─── Services sub-section ─────────────────────────────────────────────────────

function AppointmentServicesSection({
  storeId,
  storeCategory,
  priceLimitBypass,
}: {
  storeId: string;
  storeCategory: string;
  priceLimitBypass: boolean;
}) {
  const user = useAdminAuth();
  const qc = useQueryClient();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [editingService, setEditingService] = useState<AppointmentService | undefined>();
  const [deleteTarget, setDeleteTarget] = useState<AppointmentService | null>(null);

  const {
    data: services,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["appointment-services", storeId],
    queryFn: () => listAppointmentServices(user!, storeId),
    enabled: !!user,
    refetchOnMount: "always",
  });

  const deleteMutation = useMutation({
    mutationFn: (s: AppointmentService) => deleteAppointmentService(user!, storeId, s.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["appointment-services", storeId] });
      setDeleteTarget(null);
      toast.success("Service deleted.");
    },
    onError: (err) => toast.error((err as Error).message),
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner className="size-4 border-primary" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-8 text-center">
        <AlertCircle className="size-6 text-muted-foreground/50" />
        <p className="text-sm text-muted-foreground">Failed to load services.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-sm">Services ({services?.length ?? 0})</h3>
        <Button
          size="sm"
          onClick={() => {
            setEditingService(undefined);
            setFormKey((k) => k + 1);
            setDialogOpen(true);
          }}
        >
          <Plus className="size-3.5 mr-1" /> Add Service
        </Button>
      </div>

      {services?.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8">No services yet.</p>
      ) : (
        <div className="space-y-2">
          {services?.map((s) => (
            <Card
              key={s.id}
              onClick={() => {
                setEditingService(s);
                setFormKey((k) => k + 1);
                setDialogOpen(true);
              }}
              className="cursor-pointer hover:border-primary/50"
            >
              <CardContent className="flex items-start gap-3 p-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-sm">{s.name}</p>
                    <StatusBadge status={s.active ? "active" : "inactive"} />
                  </div>
                  {s.description && (
                    <p className="text-xs text-muted-foreground line-clamp-1">{s.description}</p>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    {s.duration_minutes} min · {formatCents(s.discounted_price_cents)}
                    {s.target_deal_active && (
                      <>
                        {" "}
                        <span className="line-through">{formatCents(s.price_cents)}</span>{" "}
                        <span className="text-green-600">({s.target_deal_name})</span>
                      </>
                    )}
                    {" · "}
                    {formatPoints(s.points_earned)} (computed)
                  </p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-7 text-muted-foreground enabled:hover:text-destructive enabled:hover:bg-destructive/10"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteTarget(s);
                    }}
                  >
                    <Trash2 className="size-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <AppointmentServiceFormDialog
        key={formKey}
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setEditingService(undefined);
        }}
        storeId={storeId}
        storeCategory={storeCategory}
        priceLimitBypass={priceLimitBypass}
        service={editingService}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(o) => {
          if (!o) setDeleteTarget(null);
        }}
        title="Delete service"
        description={`Delete "${deleteTarget?.name}"? This cannot be undone.`}
        confirmLabel="Delete"
        destructive
        isPending={deleteMutation.isPending}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget)}
      />
    </div>
  );
}

// ─── Bookings sub-section ──────────────────────────────────────────────────────

function AppointmentBookingsSection({ storeId }: { storeId: string }) {
  const user = useAdminAuth();
  const qc = useQueryClient();
  const [offset, setOffset] = useState(0);
  const [toCancel, setToCancel] = useState<Appointment | null>(null);
  const [toMarkNoShow, setToMarkNoShow] = useState<Appointment | null>(null);
  const [toComplete, setToComplete] = useState<Appointment | null>(null);
  const [toAdjust, setToAdjust] = useState<Appointment | null>(null);
  const [adjustAmount, setAdjustAmount] = useState("");
  const [toBan, setToBan] = useState<Appointment | null>(null);
  const [banReason, setBanReason] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["appointments", storeId, offset],
    queryFn: () => listStoreAppointments(user!, storeId, { limit: PAGE_SIZE, offset }),
    enabled: !!user,
    refetchOnMount: "always",
  });

  const cancelMutation = useMutation({
    mutationFn: (a: Appointment) => cancelAppointment(user!, storeId, a.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["appointments", storeId] });
      setToCancel(null);
      toast.success("Appointment cancelled.");
    },
    onError: (err) => toast.error((err as Error).message),
  });

  const noShowMutation = useMutation({
    mutationFn: (a: Appointment) => markAppointmentNoShow(user!, storeId, a.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["appointments", storeId] });
      setToMarkNoShow(null);
      toast.success("Appointment marked as no-show.");
    },
    onError: (err) => toast.error((err as Error).message),
  });

  const completeMutation = useMutation({
    mutationFn: (a: Appointment) => completeAppointment(user!, storeId, a.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["appointments", storeId] });
      setToComplete(null);
      toast.success("Appointment marked as completed.");
    },
    onError: (err) => toast.error((err as Error).message),
  });

  const adjustMutation = useMutation({
    mutationFn: (amountCents: number) =>
      completeAppointment(user!, storeId, toAdjust!.id, amountCents),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["appointments", storeId] });
      setToAdjust(null);
      setAdjustAmount("");
      toast.success("Price adjusted.");
    },
    onError: (err) => toast.error((err as Error).message),
  });

  const banMutation = useMutation({
    mutationFn: (a: Appointment) => imposeStoreAppointmentBan(user!, storeId, a.id, banReason),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["appointment-bans", storeId] });
      setToBan(null);
      setBanReason("");
      toast.success("Customer banned from booking at this store.");
    },
    onError: (err) => toast.error((err as Error).message),
  });

  const columns: Column<Appointment>[] = [
    {
      key: "customer",
      header: "Customer",
      render: (a) =>
        a.user_uid ? (
          <Link
            to={`/users/${a.user_uid}`}
            className="text-sm font-medium hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {`${a.user_first_name ?? ""} ${a.user_last_name ?? ""}`.trim() || a.user_uid}
          </Link>
        ) : a.guest_phone ? (
          <span className="text-sm font-medium">{`Guest (${a.guest_phone})`}</span>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        ),
    },
    {
      key: "staff",
      header: "Staff",
      render: (a) => <span className="text-sm">{a.staff_name ?? "—"}</span>,
    },
    {
      key: "service",
      header: "Service",
      render: (a) => <span className="text-sm">{a.service_name ?? "—"}</span>,
    },
    {
      key: "price",
      header: "Price",
      render: (a) =>
        a.status === "completed" && a.charged_price_cents != null ? (
          <div className="text-sm">
            {formatCents(a.charged_price_cents)}
            {a.service_price_cents != null && a.service_price_cents !== a.charged_price_cents && (
              <span className="ml-1 line-through">{formatCents(a.service_price_cents)}</span>
            )}
            {a.price_adjusted && (
              <Badge
                variant="outline"
                className="ml-1.5 text-purple-600 border-purple-200 bg-purple-50"
              >
                Adjusted
              </Badge>
            )}
          </div>
        ) : a.service_discounted_price_cents != null ? (
          <div className="text-sm text-muted-foreground">
            {formatCents(a.service_discounted_price_cents)}
            {a.service_price_cents != null &&
              a.service_price_cents !== a.service_discounted_price_cents && (
                <span className="ml-1 line-through">{formatCents(a.service_price_cents)}</span>
              )}
          </div>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        ),
    },
    {
      key: "time",
      header: "Time",
      render: (a) => (
        <span className="text-sm text-muted-foreground">{formatDateTime(a.starts_at)}</span>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (a) => <StatusBadge status={a.status} />,
    },
    {
      key: "actions",
      header: "",
      className: "sticky right-0 bg-background border-l",
      render: (a) => (
        <div className="flex justify-end">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted"
                aria-label="Actions"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreHorizontal className="size-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {a.status === "booked" && (
                <>
                  <DropdownMenuItem onClick={() => setToComplete(a)}>
                    Mark Completed
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setToAdjust(a)}>Adjust Price</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setToMarkNoShow(a)}>No-show</DropdownMenuItem>
                  <DropdownMenuItem variant="destructive" onClick={() => setToCancel(a)}>
                    Cancel
                  </DropdownMenuItem>
                </>
              )}
              {(a.user_uid || a.guest_phone) && (
                <DropdownMenuItem variant="destructive" onClick={() => setToBan(a)}>
                  Ban Customer
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <DataTable
        columns={columns}
        data={data?.data ?? []}
        isLoading={isLoading}
        rowKey={(a) => a.id}
        emptyMessage="No appointments yet."
      />

      {data && (
        <Pagination
          offset={offset}
          limit={PAGE_SIZE}
          total={data.total}
          onOffsetChange={setOffset}
        />
      )}

      <ConfirmDialog
        open={!!toCancel}
        onOpenChange={(o) => {
          if (!o) setToCancel(null);
        }}
        title="Cancel appointment?"
        description="The customer will be notified that their appointment was cancelled. This is never window-gated and never penalizes the customer."
        confirmLabel="Cancel Appointment"
        destructive
        isPending={cancelMutation.isPending}
        onConfirm={() => toCancel && cancelMutation.mutate(toCancel)}
      />

      <ConfirmDialog
        open={!!toMarkNoShow}
        onOpenChange={(o) => {
          if (!o) setToMarkNoShow(null);
        }}
        title="Mark as no-show?"
        description="This flags the appointment as a no-show immediately instead of waiting for the automatic grace period."
        confirmLabel="Mark No-Show"
        destructive
        isPending={noShowMutation.isPending}
        onConfirm={() => toMarkNoShow && noShowMutation.mutate(toMarkNoShow)}
      />

      <ConfirmDialog
        open={!!toBan}
        onOpenChange={(o) => {
          if (!o) {
            setToBan(null);
            setBanReason("");
          }
        }}
        title="Ban customer from booking"
        description="Ban the customer behind this appointment from booking again at this store? This does not affect their access to any other store on DISCA."
        confirmLabel="Ban"
        destructive
        isPending={banMutation.isPending}
        onConfirm={() => toBan && banMutation.mutate(toBan)}
        textarea={{
          value: banReason,
          onChange: setBanReason,
          label: "Reason (optional)",
          placeholder: "e.g. repeated no-shows",
          maxLength: 500,
        }}
      />

      <ConfirmDialog
        open={!!toComplete}
        onOpenChange={(o) => {
          if (!o) setToComplete(null);
        }}
        title="Mark appointment as completed?"
        description="Use this to check in a customer without scanning a QR code — the only way to check in a guest booking. Awards points/stamps the same as a QR scan for an account-holder booking."
        confirmLabel="Mark Completed"
        isPending={completeMutation.isPending}
        onConfirm={() => toComplete && completeMutation.mutate(toComplete)}
      />

      <ConfirmDialog
        open={!!toAdjust}
        onOpenChange={(o) => {
          if (!o) {
            setToAdjust(null);
            setAdjustAmount("");
          }
        }}
        title="Adjust Price"
        description="Charge a different amount than what was booked — e.g. because the service performed differed — instead of scanning a QR code. This is also the only way to adjust the price for a guest booking."
        confirmLabel="Confirm"
        isPending={adjustMutation.isPending}
        onConfirm={() => {
          const amount = parseFloat(adjustAmount);
          if (!amount || amount <= 0) {
            toast.error("Enter a valid amount.");
            return;
          }
          adjustMutation.mutate(Math.round(amount * 100));
        }}
        confirmInput={{
          value: adjustAmount,
          onChange: (v) => setAdjustAmount(v.replace(/[^\d.]/g, "").replace(/(\..*)\./g, "$1")),
          placeholder: "e.g. 45.00",
        }}
      />
    </div>
  );
}
